import { useEffect, useState } from 'react';
import { REQUEST_METHOD } from '@laramie/ui';
import { APIS } from '../config/apis';
import { ServerRequest } from '../utils/apiweb';
import { isValidCuit } from '../utils/validator';
import { TIPO_DOCUMENTO } from '../consts/entityType';

const unir = (...listas) => [...new Set(listas.flat())];
// Sólo un D.N.I. se compara sin los ceros de adelante: ARCA no los trae, y hay D.N.I. guardados con un cero adelante
// (`08123456` es `8123456`). Un CUIT se compara tal cual: uno rellenado con ceros no es el de ARCA
const sinCeros = numero => String(numero ?? '').replace(/^0+/, '');
const mismoNumero = (idTipoDocumento, a, b) => (idTipoDocumento === TIPO_DOCUMENTO.DNI)
    ? sinCeros(a) === sinCeros(b)
    : String(a ?? '') === String(b ?? '');

/**
 * Identificación con la que se consulta ARCA: el CUIT de la grilla de documentos y, si no hay y se admite,
 * el DNI; primero el principal. Los demás tipos (sin documento, organismos oficiales, pasaporte…) no
 * identifican a nadie en ARCA, y tampoco un CUIT con un prefijo que AFIP no da o que no cumple el dígito
 * verificador: en Olavarría la migración cargó como CUIT números internos de RAFAM (LAR-2361), y con esos se
 * consulta por el DNI. `cabecera` ({ idTipoDocumento, numeroDocumento }), si se pasa, es el último recurso: se usa
 * sólo si la grilla no identifica a la persona. Hay jurídicas, y físicas migradas en Olavarría, con el CUIT sólo en
 * el encabezado, sin una fila en la grilla.
 * Si se admite el DNI y no hay un CUIT válido ni un DNI, en la grilla ni en el encabezado, se consulta como DNI con una
 * L.E. o una L.C., primero de la grilla: ARCA busca un documento sólo por su número.
 */
export function identificacionArca(documentos, conDni, cabecera = null) {
    const numero = (candidatos, tipos, admitido) => candidatos
        .filter(documento => tipos.includes(parseInt(documento.idTipoDocumento)))
        .map(documento => String(documento.numeroDocumento ?? '').replace(/\D/g, ''))
        .find(admitido);
    const comoDni = valor => /^\d{7,8}$/.test(valor);

    const identificar = candidatos => {
        const cuit = numero(candidatos, [TIPO_DOCUMENTO.CUIT], isValidCuit);
        if (cuit) return { tipo: 'CUIT', numero: cuit };
        const dni = conDni ? numero(candidatos, [TIPO_DOCUMENTO.DNI], comoDni) : null;
        return dni ? { tipo: 'DNI', numero: dni } : null;
    };
    const libreta = candidatos => {
        const lelc = numero(candidatos, [TIPO_DOCUMENTO.LE, TIPO_DOCUMENTO.LC], comoDni);
        return lelc ? { tipo: 'DNI', numero: lelc } : null;
    };
    const grilla = documentos.filter(documento => documento.state !== 'r')
        .sort((a, b) => (b.principal ? 1 : 0) - (a.principal ? 1 : 0));
    const encabezado = cabecera ? [cabecera] : [];
    return identificar(grilla) ?? identificar(encabezado) ?? (conDni ? libreta(grilla) ?? libreta(encabezado) : null);
}

/**
 * Documentos de ARCA que se agregan a la grilla: los elegidos que la persona todavía no tiene (el número de un D.N.I. se
 * compara sin los ceros de adelante). Nunca se quita ni se cambia uno que ya estaba; el agregado pasa a principal sólo si
 * no queda ningún principal.
 * Con la grilla vacía, el principal es el documento del encabezado (`cabecera`, si se pasa): va primero y, si no
 * es uno de los de ARCA, pasa a la grilla con ellos, así ninguno lo saca del encabezado. Si es uno de ellos, pasa como
 * está guardado cuando sólo difiere en los ceros de adelante (el D.N.I. igual al de ARCA, que no se reescribe), y como
 * lo trae ARCA si está escrito de otra forma (un CUIT con guiones).
 */
export function documentosArca(campos, persona, documentos, { idTipoPersona, idPersona, idInicial, cabecera = null }) {
    const vigentes = documentos.filter(documento => documento.state !== 'r');
    const elegidos = [
        campos.includes('cuit') && persona.cuit ? { idTipoDocumento: TIPO_DOCUMENTO.CUIT, numeroDocumento: persona.cuit } : null,
        campos.includes('dni') && persona.tipoDocumento === 'DNI' && persona.numeroDocumento ? { idTipoDocumento: TIPO_DOCUMENTO.DNI, numeroDocumento: persona.numeroDocumento } : null,
    ].filter(documento => documento && !vigentes.some(f =>
        parseInt(f.idTipoDocumento) === documento.idTipoDocumento && mismoNumero(documento.idTipoDocumento, f.numeroDocumento, documento.numeroDocumento)));

    const digitos = numero => String(numero ?? '').replace(/\D/g, '');
    const conEncabezado = vigentes.length === 0 && elegidos.length > 0 && parseInt(cabecera?.idTipoDocumento) > 0 && digitos(cabecera.numeroDocumento).length > 0;
    const esEncabezado = documento => parseInt(cabecera.idTipoDocumento) === documento.idTipoDocumento
        && mismoNumero(documento.idTipoDocumento, digitos(cabecera.numeroDocumento), digitos(documento.numeroDocumento));
    const delEncabezado = documento => mismoNumero(documento.idTipoDocumento, cabecera.numeroDocumento, documento.numeroDocumento)
        ? { ...documento, numeroDocumento: cabecera.numeroDocumento } : documento;
    const agregados = !conEncabezado ? elegidos
        : elegidos.some(esEncabezado) ? [...elegidos.filter(esEncabezado).map(delEncabezado), ...elegidos.filter(documento => !esEncabezado(documento))]
        : [{ idTipoDocumento: parseInt(cabecera.idTipoDocumento), numeroDocumento: cabecera.numeroDocumento }, ...elegidos];

    let conPrincipal = vigentes.some(documento => documento.principal);
    return agregados.map((documento, index) => {
        const principal = !conPrincipal;
        conPrincipal = true;
        return { id: -(idInicial + index), idTipoPersona, idPersona, ...documento, principal, state: 'a' };
    });
}

/**
 * Un domicilio de ARCA cuenta como completado por ARCA (y se bloquea al volver a abrir la persona) sólo si
 * trae todo lo que el formulario pide; si le falta algo, lo completa el operador y queda editable.
 */
export function domicilioArcaCompleto(domicilio) {
    return !!domicilio && domicilio.idProvinciaLocal > 0 && domicilio.idLocalidadLocal > 0
        && !!domicilio.calle && !!domicilio.numero && !!domicilio.codigoPostal;
}

/**
 * Actualización desde ARCA de una persona que ya existe (LAR-1948), compartida por PersonaFisicaView y
 * PersonaJuridicaView: el operador consulta ARCA desde la edición, elige qué datos aplicar y los confirma al
 * guardar, como cualquier edición.
 *
 * - La acción se ofrece sólo si ARCA está habilitada en el municipio (GET /arca/estado).
 * - Lo que ARCA completó queda bloqueado: lo guardado en la persona (`camposArca`) y lo aplicado en esta
 *   edición, salvo los domicilios aplicados en esta edición, que siguen editables hasta guardar (aunque ya
 *   estuvieran bloqueados) para poder georreferenciarlos a mano si no se encuentran solos.
 * - Un domicilio aplicado reemplaza al que había, también en su bloqueo: se guarda bloqueado sólo si el último
 *   domicilio de ARCA que se aplicó vino completo, aunque el anterior estuviera bloqueado.
 * - Con el mismo criterio, la vista puede dar por reemplazado el bloqueo de otros campos que ARCA no completó (en la
 *   física, nombre y apellido cuando ARCA trae el nombre entero en uno de los dos, LAR-1776; en las dos, un domicilio
 *   igual al de ARCA que ARCA trae incompleto, que no se reescribe): quedan editables aunque estuvieran bloqueados, y
 *   se bloquean sólo si una aplicación posterior de esta edición los completa.
 * - Cada domicilio aplicado se cuenta (`domicilioAplicado(campo).vez`): su formulario se vuelve a montar en
 *   cada aplicación para que lo georreferencie de nuevo.
 * - Una persona dada de alta con ARCA antes de esta acción tiene `camposArca` en null: se bloquea como
 *   hasta ahora (`camposAlta`).
 *
 * @param {Object} config
 * @param {Object} config.persona - la persona como se cargó, con su `origen` y sus `camposArca` guardados
 * @param {boolean} config.disponible - edición de una persona guardada que no validó su identidad por VU
 * @param {string[]} [config.camposAlta] - lo que se bloquea en una persona dada de alta con ARCA
 * @param {string[]} [config.domicilios] - los campos de domicilio de la persona
 */
export default function useArcaActualizacion({ persona, disponible, camposAlta = [], domicilios = [] }) {
    const [habilitada, setHabilitada] = useState(false);
    const [abierta, setAbierta] = useState(false);
    // null mientras no se aplicó nada en esta edición
    const [completados, setCompletados] = useState(null);
    // domicilios de ARCA aplicados en esta edición, por campo: { domicilio, vez }
    const [domiciliosAplicados, setDomiciliosAplicados] = useState({});
    // campos cuyo bloqueo anterior (el guardado o el de una aplicación anterior) reemplazó una aplicación de esta
    // edición: los domicilios aplicados y los que indicó la vista
    const [bloqueosReemplazados, setBloqueosReemplazados] = useState([]);

    useEffect(() => {
        if (!disponible) return;
        ServerRequest(
            REQUEST_METHOD.GET, null, true,
            APIS.URLS.ARCA, '/estado', null,
            (response) => {
                response.json()
                .then(data => setHabilitada(data?.habilitado === true))
                .catch(() => setHabilitada(false));
            },
            () => setHabilitada(false),
            () => setHabilitada(false)
        );
    }, [disponible]);

    const guardados = (persona.origen !== 'ARCA') ? []
        : (persona.camposArca === null || persona.camposArca === undefined) ? camposAlta
        : persona.camposArca.split(',').filter(campo => campo.length > 0);

    // Lo que ARCA completó en esta edición se bloquea enseguida, salvo un domicilio, que sigue editable hasta guardar
    function bloqueado(campo) {
        const anterior = guardados.includes(campo) && !bloqueosReemplazados.includes(campo);
        return anterior || (!domicilios.includes(campo) && !!completados && completados.includes(campo));
    }

    // `campos`: los que ARCA completó; `aplicados`: los domicilios de ARCA que pasaron al formulario, por campo;
    // `reemplazados`: los campos que esta aplicación deja sin su bloqueo anterior aunque no los completó.
    // Un domicilio aplicado otra vez cuenta como completado sólo si el último vino completo, y un campo
    // reemplazado, sólo si una aplicación posterior lo completa.
    function registrar(campos, aplicados = {}, reemplazados = []) {
        const sinBloqueoAnterior = unir(Object.keys(aplicados), reemplazados);
        setCompletados(prev => unir((prev ?? []).filter(campo => !sinBloqueoAnterior.includes(campo)), campos));
        setBloqueosReemplazados(prev => unir(prev, sinBloqueoAnterior));
        setDomiciliosAplicados(prev => ({
            ...prev,
            ...Object.fromEntries(Object.entries(aplicados).map(([campo, domicilio]) =>
                [campo, { domicilio, vez: (prev[campo]?.vez ?? 0) + 1 }])),
        }));
    }

    // Lo que se guarda con la persona si se aplicó algo de ARCA: la marca y los campos que completó. Un campo
    // reemplazado en esta edición (un domicilio, el nombre partido) no hereda el bloqueo anterior: lo tiene sólo si
    // está en `completados`.
    function marca() {
        if (completados === null) return {};
        const conservados = guardados.filter(campo => !bloqueosReemplazados.includes(campo));
        return { origen: 'ARCA', camposArca: unir(conservados, completados).join(',') };
    }

    return {
        visible: disponible && habilitada,
        abierta,
        abrir: () => setAbierta(true),
        cerrar: () => setAbierta(false),
        bloqueado,
        registrar,
        marca,
        domicilioAplicado: campo => domiciliosAplicados[campo] ?? null,
        reiniciar: () => {
            setCompletados(null);
            setDomiciliosAplicados({});
            setBloqueosReemplazados([]);
        },
    };
}
